// ─────────────────────────────────────────────────────────────────
// Glass-i — Glass Type Master helpers (Stage 6B.8A-3M).
//
// Pure JS — tested by the frontend node:test runner.
// ─────────────────────────────────────────────────────────────────

// Mirrors backend migration 0052 seeds. Used as a frontend fallback
// only when the API has not yet returned (or returned empty).
export const DEFAULT_GLASS_TYPES = Object.freeze([
  { sno: 1,  glassType: 'A-BLOCK'    },
  { sno: 2,  glassType: 'A-DRAW'     },
  { sno: 3,  glassType: 'A-TEMPLATE' },
  { sno: 4,  glassType: 'A-SHAPE'    },
  { sno: 5,  glassType: 'A-DGU'      },
  { sno: 6,  glassType: 'A-LAMI'     },
  { sno: 7,  glassType: 'T-BLOCK'    },
  { sno: 8,  glassType: 'T-DRAWING'  },
  { sno: 9,  glassType: 'T-SHAPE'    },
  { sno: 10, glassType: 'T-TEMPLATE' },
  { sno: 11, glassType: 'T-DGU'      },
  { sno: 12, glassType: 'T-LAMI'     },
  { sno: 13, glassType: 'T+A+DGU'    },
  { sno: 14, glassType: 'T+A+LAMI'   },
  { sno: 15, glassType: 'T-HS'       }
]);

// ── normalizeGlassTypeRows ──────────────────────────────────────
// Coerces API row shape into the editor's working shape and assigns
// a stable _clientRowId so React keys never collide while editing a
// brand-new (server-id-less) row.
let _rowIdCounter = 0;
function nextRowId() { _rowIdCounter += 1; return `gt-c-${_rowIdCounter}`; }

export function normalizeGlassTypeRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    _clientRowId: r._clientRowId || (r.id ? `gt-${r.id}` : nextRowId()),
    _dirty: false,
    id:        r.id || null,
    sno:       r.sno != null ? Number(r.sno) : null,
    glassType: r.glassType || '',
    isActive:  r.isActive !== false,
    remarks:   r.remarks || ''
  }));
}

export function markDirtyRow(rows, clientRowId, patch) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => r._clientRowId === clientRowId
    ? { ...r, ...patch, _dirty: true }
    : r);
}

// ── nextSno ─────────────────────────────────────────────────────
// Suggest the next sno for a brand-new row: max(existing) + 1, or 1
// when the grid is empty.
export function nextSno(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 1;
  let max = 0;
  for (const r of rows) {
    const n = Number(r && r.sno);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

// ── sortGlassTypes ──────────────────────────────────────────────
export function sortGlassTypes(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const as = a && a.sno != null ? Number(a.sno) : Number.POSITIVE_INFINITY;
    const bs = b && b.sno != null ? Number(b.sno) : Number.POSITIVE_INFINITY;
    if (as !== bs) return as - bs;
    return String((a && a.glassType) || '')
      .localeCompare(String((b && b.glassType) || ''));
  });
}

// ── Payload builders ────────────────────────────────────────────
export function buildGlassTypePayload(row) {
  if (!row || typeof row !== 'object') return {};
  const out = {
    glassType: trimOrNull(row.glassType),
    isActive:  row.isActive !== false,
    remarks:   trimOrNull(row.remarks)
  };
  if (row.sno != null && row.sno !== '') out.sno = Math.trunc(Number(row.sno));
  return out;
}

// `rows` is the entire editor working set; emits `{ rows, deletes }`
// shaped for the backend bulk-save endpoint. Only dirty rows + queued
// deletes go on the wire — clean rows are skipped to keep the
// payload tight and the audit row meaningful.
export function buildGlassTypeBulkPayload(rows, deletedIds = []) {
  const dirty = (rows || []).filter((r) => r && r._dirty);
  return {
    rows: dirty.map((r) => {
      const out = buildGlassTypePayload(r);
      if (r.id) out.id = r.id;
      if (r._clientRowId) out._clientRowId = r._clientRowId;
      return out;
    }),
    deletes: Array.isArray(deletedIds)
      ? deletedIds.filter((id) => typeof id === 'string' && id.length > 0)
      : []
  };
}

// ── Validation ──────────────────────────────────────────────────
// Lightweight last-mile checks before opening the network call.
// Returns { ok, errors: [{ field, message, clientRowId? }] }.
export function validateGlassTypeRow(row, allRows = []) {
  const errors = [];
  if (!row || typeof row !== 'object') {
    return { ok: false, errors: [
      { field: 'row', message: 'Row is required.' }
    ] };
  }
  const name = String(row.glassType || '').trim();
  if (!name) {
    errors.push({ field: 'glassType',
      message: 'Glass Type is required.', clientRowId: row._clientRowId });
  }
  if (row.sno != null && row.sno !== '') {
    const n = Number(row.sno);
    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
      errors.push({ field: 'sno',
        message: 'S No must be greater than zero.',
        clientRowId: row._clientRowId });
    }
  }
  // Cross-row duplicate checks (case-insensitive name + sno).
  if (Array.isArray(allRows) && allRows.length > 0) {
    const peers = allRows.filter((r) =>
      r && r._clientRowId !== row._clientRowId);
    const sno = Number(row.sno);
    if (Number.isFinite(sno) && sno > 0) {
      for (const p of peers) {
        if (Number(p.sno) === sno) {
          errors.push({ field: 'sno',
            message: `S No ${sno} is already used by another row.`,
            clientRowId: row._clientRowId });
          break;
        }
      }
    }
    if (name) {
      const lower = name.toLowerCase();
      for (const p of peers) {
        if (String(p.glassType || '').trim().toLowerCase() === lower) {
          errors.push({ field: 'glassType',
            message: `Glass Type "${name}" already exists.`,
            clientRowId: row._clientRowId });
          break;
        }
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateGlassTypeRows(rows) {
  const all = Array.isArray(rows) ? rows : [];
  const errors = [];
  for (const r of all) {
    const { ok, errors: rowErrors } = validateGlassTypeRow(r, all);
    if (!ok) errors.push(...rowErrors);
  }
  return { ok: errors.length === 0, errors };
}

// ── Private helpers ─────────────────────────────────────────────
function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}
